import type { Router, RouteRecordRaw } from 'vue-router';

import { usePermissionStoreWithOut } from '/@/store/modules/permission';

import { PackageEnum, PageEnum } from '/@/enums/pageEnum';
import { useUserStoreWithOut } from '/@/store/modules/user';

import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';

import { RootRoute } from '/@/router/routes';

const LOGIN_PATH = PageEnum.BASE_LOGIN;

const ROOT_PATH = RootRoute.path;

const whitePathList: PageEnum[] = [
  LOGIN_PATH,
  PageEnum.FORBIDDEN_PAGE,
  PageEnum.HOME,
  PageEnum.SYS_UPDATE,
  PageEnum.QUESTIONNAIRE,
  PageEnum.QUESTIONNAIRE_IPI,
  PageEnum.QUESTIONNAIRE_CPI,
  PageEnum.QUESTIONNAIRE_INFO,
  PageEnum.MARKET,
  PageEnum.MARKET_INDEX,
  PageEnum.MARKET_PACKAGE_ASSEMBLY,
  `${PageEnum.MARKET_DRO_DETAIL}/:droCode/:listed?` as PageEnum,
  PageEnum.MARKET_ALL_MARKET,
  `${PageEnum.MARKET_PACKAGE_ASSEMBLY_DETAIL}/:seqId?/:groupId?` as PageEnum,
  `${PageEnum.MARKET_ALL_DRO}/:brandId?` as PageEnum,
  PageEnum.MARKET_ALL_BRAND,
];

export function createPermissionGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const permissionStore = usePermissionStoreWithOut();
  router.beforeEach(async (to, from, next) => {
    /**
     * 根目录跳转到系统首页时，用户已经登录并存在用户首页且用户首页不是系统首页
     * 则直接跳转到用户首页
     */
    if (
      from.path === ROOT_PATH &&
      to.path === PageEnum.BASE_HOME &&
      userStore.getUserInfo.homePath &&
      userStore.getUserInfo.homePath !== PageEnum.BASE_HOME
    ) {
      next(userStore.getUserInfo.homePath);
      return;
    }

    const token = userStore.getToken;
    /**
     * 带参数路由是否是白名单判断，取matched匹配上的最后一条记录
     */
    const matchedPath = to.matched[to.matched.length - 1].path as PageEnum;

    /**
     * 白名单可以直接进入
     */
    if (whitePathList.includes(to.path as PageEnum) || whitePathList.includes(matchedPath)) {
      // 如果去往的页面是登录页面则尝试执行登录后的动作
      if (to.path === LOGIN_PATH && token) {
        const isSessionTimeout = userStore.getSessionTimeout;
        try {
          await userStore.afterLoginAction();
          if (!isSessionTimeout) {
            next((to.query?.redirect as string) || '/');
            return;
          }
        } catch {}
      }
      // 如果已经登录了且是home 就去登录后的首页
      if (token && to.path === PageEnum.HOME) {
        next(PageEnum.BASE_HOME);
        return;
      }
      // 如果未登录且是home
      if (!token && to.path === PageEnum.HOME) {
        await permissionStore.setFrontMenuList([]);
        next();
        return;
      }

      // 已登录则获取菜单
      if (token) {
        await userStore.afterLoginAction();
        next();
        return;
      }
      next();
      return;
    }

    // 未登录时
    if (!token) {
      // 路由meta.ignoreAuth设置为true可以在未经许可的情况下访问。
      if (to.meta.ignoreAuth) {
        next();
        return;
      }

      // 未登录默认跳往游客页面
      let redirectPath = PageEnum.HOME;

      // 从游客页面跳转去往其它页面时需要重定向到登录页面
      if (PageEnum.HOME === from.path) {
        if (userStore.keycloak) {
          userStore.keycloak?.login({ redirectUri: window.location.origin + '/' + to.path });
          return;
        }
        redirectPath = LOGIN_PATH;
      }

      // 如果package设置了则跳往登录页
      const path = to.path.split('/')[1];
      if (PackageEnum[path]) {
        redirectPath = LOGIN_PATH;
      }

      // redirect login page
      const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
        path: redirectPath,
        replace: true,
      };
      if (to.path) {
        redirectData.query = {
          ...redirectData.query,
          redirect: to.path,
        };
      }

      next(redirectData);
      return;
    }

    // 处理登录后跳转到404页面时重定向到首页
    if (
      from.path === LOGIN_PATH &&
      to.name === PAGE_NOT_FOUND_ROUTE.name &&
      to.fullPath !== (userStore.getUserInfo.homePath || PageEnum.BASE_HOME)
    ) {
      next(userStore.getUserInfo.homePath || PageEnum.BASE_HOME);
      return;
    }

    // get userinfo while last fetch time is empty
    if (userStore.getLastUpdateTime === 0) {
      try {
        await userStore.getUserInfoAction();
      } catch (err) {
        next();
        return;
      }
    }

    if (permissionStore.getIsDynamicAddedRoute) {
      next();
      return;
    }

    const routes = await permissionStore.buildRoutesAction();

    routes.forEach((route) => {
      router.addRoute(route as unknown as RouteRecordRaw);
    });

    router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);

    permissionStore.setDynamicAddedRoute(true);

    if (to.name === PAGE_NOT_FOUND_ROUTE.name) {
      // 动态添加路由后，此处应当重定向到fullPath，否则会加载404页面内容
      next({ path: to.fullPath, replace: true, query: to.query });
    } else {
      const redirectPath = (from.query.redirect || to.path) as string;
      const redirect = decodeURIComponent(redirectPath);
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect };
      next(nextData);
    }
  });
}
